// Procedural brass cabinet that grows around the display and reel window (docs/07 §1.2).
// Pure palette rects, no blending: a pixel-art "9-slice" drawn in code.
import { Container, Graphics } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import type { FormId } from '../../sim/types';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CabinetLayout {
  body: Rect;
  display: Rect;
  window: Rect;
  tray: Rect;
}

const P = PALETTE;

/** Colours of each Molt form (docs/04 §20): the shell the machine wears. */
interface FormColors {
  light: number;
  mid: number;
  dark: number;
  panel: [number, number, number];
  inset: [number, number, number];
  rivet: [number, number];
  bulbOn: number;
  bulbOff: number;
}

export const FORM_COLORS: Record<FormId, FormColors> = {
  brass: { light: P.brass, mid: P.bronze, dark: P.copper, panel: [P.copper, P.brick, P.wine], inset: [P.brick, P.mud, P.bronze], rivet: [P.lemon, P.copper], bulbOn: P.lemon, bulbOff: P.brick },
  bone: { light: P.white, mid: P.paper, dark: P.khaki, panel: [P.sand, P.mauveGrey, P.plumGrey], inset: [P.steel, P.dusk, P.paper], rivet: [P.white, P.sand], bulbOn: P.ice, bulbOff: P.dusk },
  flesh: { light: P.coral, mid: P.dustyPink, dark: P.rose, panel: [P.rose, P.mulberry, P.wine], inset: [P.mulberry, P.crimson, P.coral], rivet: [P.cream, P.crimson], bulbOn: P.salmonPink, bulbOff: P.wine },
  gilded: { light: P.lemon, mid: P.gold, dark: P.amber, panel: [P.night, P.ink, P.ink], inset: [P.amber, P.ink, P.gold], rivet: [P.white, P.amber], bulbOn: P.white, bulbOff: P.amber },
};

function bevel(g: Graphics, r: Rect, light: number, mid: number, dark: number, t = 2): void {
  g.rect(r.x, r.y, r.w, r.h).fill(mid);
  g.rect(r.x, r.y, r.w, t).fill(light);
  g.rect(r.x, r.y, t, r.h).fill(light);
  g.rect(r.x, r.y + r.h - t, r.w, t).fill(dark);
  g.rect(r.x + r.w - t, r.y, t, r.h).fill(dark);
}

function inset(g: Graphics, r: Rect, pad: number, c: FormColors): void {
  const o = { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
  g.rect(o.x - 1, o.y - 1, o.w + 2, o.h + 2).fill(P.ink);
  bevel(g, o, c.inset[0], c.inset[1], c.inset[2], 2);
  g.rect(r.x - 1, r.y - 1, r.w + 2, r.h + 2).fill(P.ink);
}

/** Ordered dither: is pixel (x, y) lit at density level 0 (none) … 4 (all)? */
function dither(x: number, y: number, level: number): boolean {
  const bayer = [0, 2, 3, 1][(x & 1) + ((y & 1) << 1)]!;
  return bayer < level;
}

export class Cabinet extends Container {
  private g = new Graphics();
  /** Drawn above the reels: the drum's shading and the glass highlight (depth pass, docs/09 Phase 6). */
  readonly glass = new Graphics();
  private bulbs = new Graphics();
  private bulbPos: { x: number; y: number }[] = [];
  private chase = 0;
  private colors: FormColors = FORM_COLORS.brass;
  form: FormId = 'brass';
  layout: CabinetLayout | null = null;

  constructor() {
    super();
    this.addChild(this.g, this.bulbs);
  }

  build(l: CabinetLayout, form: FormId = this.form): void {
    this.layout = l;
    this.form = form;
    const c = (this.colors = FORM_COLORS[form]);
    const g = this.g.clear();
    const b = l.body;
    // Depth: a dithered floor shadow, then the right side panel seen in slight perspective.
    for (let y = b.y + b.h + 2; y < b.y + b.h + 8; y++)
      for (let x = b.x - 4; x < b.x + b.w + 16; x++) if (dither(x, y, y < b.y + b.h + 5 ? 3 : 1)) g.rect(x, y, 1, 1).fill(P.ink);
    const R = b.x + b.w + 1;
    const side = 8;
    g.poly([R, b.y - 1, R + side + 1, b.y + side - 1, R + side + 1, b.y + b.h + 5, R, b.y + b.h + 2]).fill(P.ink);
    g.poly([R, b.y + 1, R + side, b.y + side, R + side, b.y + b.h + 3, R, b.y + b.h]).fill(c.dark);
    for (let y = b.y + side; y < b.y + b.h + 2; y += 3) g.rect(R + side - 2, y, 1, 1).fill(c.panel[1]);
    // outline
    g.rect(b.x - 2, b.y - 2, b.w + 4, b.h + 4).fill(P.ink);
    // main body with bevels, darker lower panel
    bevel(g, b, c.light, c.mid, c.dark, 3);
    g.rect(b.x + 3, b.y + Math.floor(b.h * 0.72), b.w - 6, 2).fill(c.panel[1]);
    bevel(g, { x: b.x + 6, y: b.y + Math.floor(b.h * 0.72) + 4, w: b.w - 12, h: b.h - Math.floor(b.h * 0.72) - 10 }, c.panel[0], c.panel[1], c.panel[2], 2);
    // rivets
    for (const [x, y] of [
      [b.x + 6, b.y + 6],
      [b.x + b.w - 8, b.y + 6],
      [b.x + 6, b.y + b.h - 8],
      [b.x + b.w - 8, b.y + b.h - 8],
    ] as const) {
      g.rect(x, y, 2, 2).fill(c.rivet[0]);
      g.rect(x + 1, y + 1, 1, 1).fill(c.rivet[1]);
    }
    // crown arch with bulbs
    const crownH = 10;
    const cw = Math.floor(b.w * 0.7);
    const cx = b.x + Math.floor((b.w - cw) / 2);
    g.rect(cx - 2, b.y - crownH - 2, cw + 4, crownH + 2).fill(P.ink);
    bevel(g, { x: cx, y: b.y - crownH, w: cw, h: crownH }, c.light, c.mid, c.dark, 2);
    this.bulbPos = [];
    const n = Math.max(5, Math.floor(cw / 12));
    for (let i = 0; i < n; i++) this.bulbPos.push({ x: cx + 5 + Math.floor((i * (cw - 12)) / (n - 1)), y: b.y - crownH + 4 });

    inset(g, l.display, 3, c);
    g.rect(l.display.x, l.display.y, l.display.w, l.display.h).fill(P.ink);
    inset(g, l.window, 4, c);
    g.rect(l.window.x, l.window.y, l.window.w, l.window.h).fill(P.ink);
    this.drawGlass(l);

    // the coin tray: a mouth with teeth
    const t = l.tray;
    if (t.h <= 0) return this.drawBulbs();
    g.rect(t.x - 1, t.y - 1, t.w + 2, t.h + 2).fill(P.ink);
    g.rect(t.x, t.y, t.w, t.h).fill(P.maroon);
    g.rect(t.x + 2, t.y + 2, t.w - 4, t.h - 4).fill(P.ink);
    for (let x = t.x + 3; x < t.x + t.w - 4; x += 5) {
      g.rect(x, t.y + 2, 3, 2).fill(P.paper);
      g.rect(x + 1, t.y + t.h - 4, 3, 2).fill(P.paper);
    }
    this.drawBulbs();
  }

  /** The reel drum's curvature (dithered shade at the top and bottom) and a dotted glass streak. */
  private drawGlass(l: CabinetLayout): void {
    const w = l.window;
    const g = this.glass.clear();
    const band = Math.min(10, Math.floor(w.h / 8));
    for (let k = 0; k < band; k++) {
      const level = k < band / 3 ? 3 : k < (band * 2) / 3 ? 2 : 1;
      for (let x = w.x; x < w.x + w.w; x++) {
        if (dither(x, w.y + k, level)) g.rect(x, w.y + k, 1, 1).fill(P.ink);
        const yb = w.y + w.h - 1 - k;
        if (dither(x, yb, level)) g.rect(x, yb, 1, 1).fill(P.ink);
      }
    }
    // Two thin diagonal glints across the glass, every other pixel so the symbols stay readable.
    for (const [x0, len] of [
      [w.x + 3, Math.floor(w.h * 0.3)],
      [w.x + 8, Math.floor(w.h * 0.18)],
    ] as const)
      for (let i = 0; i < len; i += 2) g.rect(x0 + Math.floor(i / 2), w.y + 2 + i, 1, 1).fill(P.white);
  }

  private drawBulbs(): void {
    const g = this.bulbs.clear();
    const b = this.layout?.body;
    this.bulbPos.forEach((p, i) => {
      const on = (i + Math.floor(this.chase)) % 3 === 0;
      g.rect(p.x - 1, p.y - 1, 4, 4).fill(P.ink);
      g.rect(p.x, p.y, 2, 2).fill(on ? this.colors.bulbOn : this.colors.bulbOff);
      // A lit bulb spills a little light onto the cabinet below it.
      if (on && b) for (let dx = -2; dx <= 3; dx++) if (dither(p.x + dx, b.y + 1, 1)) g.rect(p.x + dx, b.y + 1, 1, 1).fill(this.colors.bulbOn);
    });
  }

  /** Chase lights; faster when excited. */
  tick(dtMs: number, speed = 1): void {
    const before = Math.floor(this.chase);
    this.chase += (dtMs / 250) * speed;
    if (Math.floor(this.chase) !== before) this.drawBulbs();
  }
}
